import SearchWrapper from '@anm/components/templates/Search';
import { ENTER_NAME } from '@anm/constants/keyCodes';
import { VIDEO_TEMPLATE_ROUTE } from '@anm/constants/routes';
import { VideoTemplatesPageContext } from '@anm/contexts/VideoTemplatesPageContext';
import changeRouter from '@anm/helpers/changeRouter';
import usePageParams from '@anm/hooks/usePageParams';
import { useCallback, useContext, useEffect, FC, KeyboardEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import routes from '../../../routes';
import { filterActions, filterSelectors } from '../../../store/modules/filter';

import { getPageFromSearch } from './helpers';
import { useSearch } from './hooks';

const { Router } = routes;

const Search: FC = () => {
  const { pageName } = useContext(VideoTemplatesPageContext);
  const { embed, name } = usePageParams();

  const dispatch = useDispatch();
  const searchName = useSelector(filterSelectors.selectSearchName);
  const [text, setText, handleChange, updateSearchName] = useSearch();
  const isSameSearchQuery = text === searchName;

  const clearText = () => {
    setText('');
    dispatch(filterActions.updateSearchName(''));
  };

  useEffect(() => {
    if (pageName !== 'search' || !name) return;

    updateFilter(name);
  }, [name, pageName]);

  useEffect(
    () => () => {
      clearText();
    },
    [pageName]
  );

  const updateFilter = useCallback(
    async (name?: string) => {
      if (isSameSearchQuery) return;

      const pageName = getPageFromSearch(text);

      if (pageName) {
        return Router.pushRoute(VIDEO_TEMPLATE_ROUTE, {
          pageName,
          ...(embed && { embed })
        });
      }

      const searchName = name || text;

      await changeRouter({
        routes,
        routeName: VIDEO_TEMPLATE_ROUTE,
        params: { pageName: 'search', ...(embed && { embed }), name: searchName, page: null, pageNumber: null }
      });

      updateSearchName();
    },
    [text, searchName, embed]
  );

  const onKeyDown = useCallback(({ key }: KeyboardEvent<HTMLInputElement>) => ENTER_NAME === key && updateFilter(), [
    updateFilter
  ]);

  const clearInput = useCallback(() => {
    changeRouter({
      routes,
      routeName: 'video-templates',
      params: { pageName: null, name: null, page: null, ...(embed && { embed }) }
    });
    clearText();
  }, [embed]);

  const selectField = useCallback((event: { target: HTMLInputElement }) => event.target.select(), []);

  const onBlur = useCallback(() => {
    updateFilter();
  }, [updateFilter]);

  return (
    <SearchWrapper
      {...{
        text,
        onBlur,
        onKeyDown,
        clearInput,
        selectField,
        handleChange,
        updateFilter
      }}
    />
  );
};

export default Search;
